import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/authContext";
import TeamFloatingNavigation from "../components/TeamFloatingNavigation";

export default function AddNotePage() {
  const { token } = useAuth();
  const [agents, setAgents] = useState([]);
  const [agentName, setAgentName] = useState("");
  const [priority, setPriority] = useState("Normal");
  const [note, setNote] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/get-users?role=Agent`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setAgents(res.data.users || res.data || []);
        setLoading(false);
      })
      .catch(() => {
        setAgents([]);
        setLoading(false);
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!agentName || !note.trim() || !priority) {
      setError("All fields are required.");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/add-notes`,
        {
          agentName,
          note: note.trim(),
          priority,
          informEmail: priority === "Urgent",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccessMessage("Note added successfully!");
      setTimeout(() => setSuccessMessage(""), 5000);

      // Reset form
      setAgentName("");
      setNote("");
      setPriority("Normal");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to add note. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-3 border-slate-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500 text-sm">Loading agents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="w-full max-w-md mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm mb-6">
          <h1 className="text-xl font-semibold text-slate-800 text-center">Add Note</h1>
          <p className="text-sm text-slate-500 text-center mt-1">Send a note to an agent</p>
        </div>

        {/* Messages */}
        {successMessage && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-md text-sm mb-4">
            ✓ {successMessage}
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
            {error}
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Agent Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Agent Name</label>
              <select
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                required
                className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white transition-all duration-150 focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-100"
              >
                <option value="">Select an agent</option>
                {agents.map((agent) => (
                  <option key={agent._id} value={agent.name}>
                    {agent.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Priority Level</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                required
                className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white transition-all duration-150 focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-100"
              >
                <option value="Normal">Normal</option>
                <option value="Urgent">Urgent</option>
              </select>
              
              {priority === "Urgent" && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-4 h-4 bg-amber-100 text-amber-600 rounded-full text-xs">!</span>
                  Urgent notes will trigger email notifications
                </p>
              )}
            </div>

            {/* Note Content */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Note Content</label>
              <textarea
                rows="4"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Write your note here..."
                required
                className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white transition-all duration-150 focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-100 resize-vertical min-h-[100px]"
              />
              <p className="text-xs text-slate-400 mt-1">{note.length} characters</p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting || !agentName || !note.trim()}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md border border-blue-500 bg-blue-500 text-white hover:bg-blue-600 hover:border-blue-600 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Adding Note...
                </>
              ) : (
                "Add Note"
              )}
            </button>
          </form>
        </div>

        {/* Footer Info */}
        <div className="text-center mt-6">
          <p className="text-xs text-slate-400">
            Notes are delivered instantly admin team
          </p>
        </div>
      </div>

      {/* Team Floating Navigation */}
      <TeamFloatingNavigation />
    </div>
  );
}
